<div class="modal-header">
  <h4 class="modal-title">{{ "products.register.missing_fender_serial.title" | translate }}</h4>
  <button ngbAutofocus class="close" aria-label="Close" (click)="activeModal.dismiss('Cross click')">
    <span aria-hidden="true">&times;</span>
  </button>
</div>
@if (savingComplete) {
  <div class="modal-body" ngbAutofocus>
    <div class="text-center">
      <fa-icon icon="check-circle" style="color: green; font-size: 50px"></fa-icon>
      <br />
      <br />
      {{ "products.register.missing_fender_serial.success_message" | translate }}
      <br />
      <br />
      <button class="btn btn-primary" type="button" (click)="viewGear()">{{ "products.register.missing_fender_serial.view_my_gear" | translate }}</button>
    </div>
  </div>
}
@if (!savingComplete) {
  <div class="modal-body" ngbAutofocus>
    <p>We couldn't find any gear with that serial number. Please double-check your entry and try again.</p>
    <!-- The rest of the intro describes the search step, so it goes away once the customer has
    picked a product or moved on to describing their gear by hand. -->
    @if (!selectedProduct && !unknownSku) {
      <p>If your gear isn't in our records yet, search for it by name below and pick it from the list. We'll add it to your account while our team verifies your serial number.</p>
      <p>Can't find it in the list? Choose "I can't find my product" and you can describe your gear yourself.</p>
    }
    @if (unknownSku && !selectedProduct) {
      <p>Tell us what you can about your gear and upload a couple of photos, and our team will identify it and add it to your account.</p>
    }
    <div class="form-group m-b-20">
      <label for="serial" class="control-label">{{ "products.register.missing_fender_serial.serial_number" | translate }}</label>
      <input type="text" disabled id="serial" class="form-control" [(ngModel)]="serial" />
      <button class="btn btn-secondary btn-sm m-t-10" type="button" (click)="reEnterSerial()">Re-Enter Serial Number</button>
    </div>

    <!-- Product picker: search the Fender catalog by name instead of asking for a SKU. -->
    @if (!selectedProduct && !unknownSku) {
      <div class="form-group m-b-20">
        <label for="productQuery" class="control-label">Which product is this?</label>
        <small class="text-muted d-block">Search for your gear by name, then pick it from the list.</small>
        <div class="d-flex m-t-5">
          <input class="form-control m-r-5" name="productQuery" id="productQuery" placeholder="e.g. Player Stratocaster" [(ngModel)]="productQuery" (keyup.enter)="searchProducts()" />
          <button class="btn btn-primary" type="button" [disabled]="searchingProducts || !productQuery.trim()" (click)="searchProducts()">
            <fa-icon [icon]="searchingProducts ? 'spinner' : 'search'" [animation]="searchingProducts ? 'spin' : undefined"></fa-icon>
          </button>
        </div>
      </div>

      @if (searchingProducts) {
        <myp-inline-loader [loading]="true" height="150px" spinnerName="missingSerialProductSearch"></myp-inline-loader>
      } @else if (productResults.length) {
        <div class="m-b-20">
          @for (product of productResults; track product.sku) {
            <myp-list-item
              [title]="product.title"
              [subData]="product.subData"
              [photo]="product.imageUrl"
              [showImage]="!!product.imageUrl"
              [imageRound]="false"
              [imageBorder]="false"
              (itemClicked)="selectProduct(product)"
            ></myp-list-item>
            <hr class="m-y-5" />
          }
          @if (productsNextToken) {
            <div class="text-center m-t-15">
              <button class="btn btn-secondary btn-sm" type="button" [disabled]="loadingMoreProducts" (click)="loadMoreProducts()">
                @if (loadingMoreProducts) {
                  <fa-icon icon="spinner" animation="spin"></fa-icon>
                  Loading more...
                } @else {
                  Show More Results
                }
              </button>
            </div>
          }
        </div>
      } @else if (productsSearched) {
        <div class="m-y-20">
          <hr />
          We couldn't find any gear matching that name. Try searching for a different name, or choose "I can't find my product" to describe your gear yourself.
          <hr />
        </div>
      }

      <button class="btn btn-secondary btn-sm" type="button" (click)="enterProductManually()">I can't find my product</button>
    }

    <!-- Chosen product confirmation. -->
    @if (selectedProduct) {
      <div class="m-y-30">
        <hr />
        Great! Here's the gear you picked.
        <div class="d-flex m-y-20 align-items-center">
          @if (selectedProduct.imageUrl) {
            <img [src]="selectedProduct.imageUrl" [alt]="selectedProduct.title" class="m-r-20" style="max-width: 200px; max-height: 200px" />
          }
          <div>
            <span class="fui-heading-font t-20">{{ selectedProduct.title }}</span>
            @if (selectedProduct.subtitle) {
              <br />
              <small class="text-muted">{{ selectedProduct.subtitle }}</small>
            }
            <br />
            <button class="btn btn-blank btn-sm p-l-0" type="button" [disabled]="saving" (click)="clearSelectedProduct()">
              {{ "products.register.missing_fender_serial.not_my_gear" | translate }}
            </button>
          </div>
        </div>
        <hr />
      </div>
    }

    <!-- Manual description of the gear, only when the customer couldn't find it in the catalog. -->
    @if (unknownSku && !selectedProduct) {
      <div class="form-group m-b-20">
        <label for="productName" class="control-label">
          {{ "products.register.missing_fender_serial.product_name" | translate }}
          <span class="text-danger">*</span>
        </label>
        <input type="text" id="productName" [placeholder]="'products.register.missing_fender_serial.product_name_placeholder' | translate" class="form-control" [(ngModel)]="productName" required />
        <small class="text-muted">{{ "products.register.missing_fender_serial.product_name_help" | translate }}</small>
      </div>
      <div class="form-group m-b-20">
        <label for="shopLink" class="control-label">
          {{ "products.register.missing_fender_serial.shop_link" | translate }}
          <small class="text-muted">{{ "products.register.missing_fender_serial.optional" | translate }}</small>
        </label>
        <input type="text" id="shopLink" class="form-control" [(ngModel)]="shopLink" />
        <small class="text-muted">{{ "products.register.missing_fender_serial.shop_link_help" | translate }}</small>
      </div>
      <div class="form-group m-b-20">
        <label for="category" class="control-label">{{ "products.register.missing_fender_serial.category" | translate }}</label>
        <select name="category" id="category" class="form-control" [(ngModel)]="category">
          <option value="electric_guitars">{{ "products.register.missing_fender_serial.electric_guitars" | translate }}</option>
          <option value="acoustic_guitars">{{ "products.register.missing_fender_serial.acoustic_guitars" | translate }}</option>
          <option value="bass_guitars">{{ "products.register.missing_fender_serial.bass_guitars" | translate }}</option>
          <option value="amplifiers">{{ "products.register.missing_fender_serial.amplifiers" | translate }}</option>
          <option value="other">{{ "products.register.missing_fender_serial.other" | translate }}</option>
        </select>
      </div>
      <button class="btn btn-blank btn-sm p-l-0 m-b-20" type="button" (click)="unknownSku = false">Search for my product instead</button>
    }

    <!-- Photos: mandatory on the manual path, and on products flagged requiresPhotos. -->
    @if (photosRequired && (selectedProduct || unknownSku)) {
      @if (selectedProduct) {
        <div class="alert alert-info">In order to verify your serial number, please upload a couple photos of your gear.</div>
      }
      <div class="form-group m-b-20">
        <label class="control-label">
          {{ "products.register.missing_fender_serial.upload_serial_photo" | translate }}
          <span class="text-danger">*</span>
        </label>
        @if (!serialImageUploader || !serialImageUploader.queue.length) {
          <div ng2FileDrop class="content-bg content-drop-zone m-b-20" [class.hovering]="hoveringFileDrops.serial" (fileOver)="fileOverBase($event, 'serial')" [uploader]="serialImageUploader">
            <div>
              <input type="file" aria-label="Upload File" accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif" ng2FileSelect [uploader]="serialImageUploader" />
              <br />
              <br />
              {{ "products.register.missing_fender_serial.drop_serial_photo" | translate }}
            </div>
          </div>
        }
        @if (convertingPhoto.serial) {
          <div class="d-flex align-items-center m-t-10">
            <fa-icon icon="spinner" [animation]="'spin'" class="m-r-5"></fa-icon>
            <span>Preparing your photo...</span>
          </div>
        }
        @if (attachedPhotoName.serial) {
          <div class="d-flex align-items-center m-t-10">
            <fa-icon icon="check-circle" class="m-r-5" style="color: green"></fa-icon>
            <span class="m-r-10">{{ attachedPhotoName.serial }}</span>
            @if (!fileUploadProgress.serialImage && !submitArgs.serialImageUrl) {
              <button class="btn btn-sm btn-danger" (click)="removeImage('serialImage')">
                {{ "products.register.missing_fender_serial.remove" | translate }}
              </button>
            }
          </div>
        }
        @if (displaySerialImage) {
          <div class="m-t-10">
            <img [src]="displaySerialImage" class="img-thumbnail" style="max-width: 200px; max-height: 200px" />
            @if (!fileUploadProgress.serialImage && !submitArgs.serialImageUrl) {
              <button class="btn btn-sm btn-danger" (click)="removeImage('serialImage')">
                {{ "products.register.missing_fender_serial.remove" | translate }}
              </button>
            }
            @if (fileUploadProgress.serialImage) {
              <div class="d-flex m-x-10 align-items-center">
                <fa-icon icon="spinner" [animation]="'spin'" class="m-r-5"></fa-icon>
                <strong>{{ fileUploadProgress.serialImage }}%</strong>
              </div>
            }
          </div>
        }
      </div>
      <div class="form-group m-b-20">
        <label class="control-label">
          {{ "products.register.missing_fender_serial.upload_gear_photo" | translate }}
          <span class="text-danger">*</span>
        </label>
        @if (!bodyImageUploader || !bodyImageUploader.queue.length) {
          <div ng2FileDrop class="content-bg content-drop-zone m-b-20" [class.hovering]="hoveringFileDrops.body" (fileOver)="fileOverBase($event, 'body')" [uploader]="bodyImageUploader">
            <div>
              <input type="file" aria-label="Upload File" accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif" ng2FileSelect [uploader]="bodyImageUploader" />
              <br />
              <br />
              {{ "products.register.missing_fender_serial.drop_gear_photo" | translate }}
            </div>
          </div>
        }
        @if (convertingPhoto.body) {
          <div class="d-flex align-items-center m-t-10">
            <fa-icon icon="spinner" [animation]="'spin'" class="m-r-5"></fa-icon>
            <span>Preparing your photo...</span>
          </div>
        }
        @if (attachedPhotoName.body) {
          <div class="d-flex align-items-center m-t-10">
            <fa-icon icon="check-circle" class="m-r-5" style="color: green"></fa-icon>
            <span class="m-r-10">{{ attachedPhotoName.body }}</span>
            @if (!fileUploadProgress.bodyImage && !submitArgs.bodyImageUrl) {
              <button class="btn btn-sm btn-danger" (click)="removeImage('bodyImage')">
                {{ "products.register.missing_fender_serial.remove" | translate }}
              </button>
            }
          </div>
        }
        @if (displayBodyImage) {
          <div class="m-t-10">
            <img [src]="displayBodyImage" class="img-thumbnail" style="max-width: 200px; max-height: 200px" />
            @if (!fileUploadProgress.bodyImage && !submitArgs.bodyImageUrl) {
              <button class="btn btn-sm btn-danger" (click)="removeImage('bodyImage')">
                {{ "products.register.missing_fender_serial.remove" | translate }}
              </button>
            }
            @if (fileUploadProgress.bodyImage) {
              <div class="d-flex m-x-10 align-items-center">
                <fa-icon icon="spinner" [animation]="'spin'" class="m-r-5"></fa-icon>
                <strong>{{ fileUploadProgress.bodyImage }}%</strong>
              </div>
            }
          </div>
        }
      </div>
    }
  </div>
}
@if ((selectedProduct || unknownSku) && !savingComplete) {
  <div class="modal-footer">
    <button type="button" class="btn btn-primary" [disabled]="saving" (click)="submit()">
      @if (!saving) {
        <span>{{ selectedProduct ? ("products.register.missing_fender_serial.add_to_account" | translate) : ("general.submit" | translate) }}</span>
      }
      @if (saving) {
        <span>
          <fa-icon icon="spinner" [animation]="'spin'"></fa-icon>
          {{ "general.saving_dots" | translate }}
        </span>
      }
    </button>
    <button type="button" class="btn btn-blank" (click)="activeModal.close('Close click')">{{ "general.close" | translate }}</button>
  </div>
}
